<script setup>
import moment from "moment";

import { mapGetters } from "vuex";

import { useDestinationStore } from "../../../store/destinationStore";
import { useOrderStore } from "../../../store/orderStore";
import { useDeviceStore } from "../../../store/deviceStore";
import { usePeriodStore } from "../../../store/periodStore";
import { useShipmentStore } from "../../../store/shipmentStore";
import { usePlanStore } from "../../../store/planStore";

import DestinationSelect from "./purchase-rent-plan/DestinationSelect.vue";
import PlanTypeSwitcher from "./purchase-rent-plan/PlanTypeSwitcher.vue";
import MultiDestinationToggle from "./purchase-rent-plan/MultiDestinationToggle.vue";
import DateSelect from "./purchase-rent-plan/DateSelect.vue";
import PlanSelect from "./purchase-rent-plan/PlanSelect.vue";
import QuantitySelect from "./purchase-rent-plan/QuantitySelect.vue";
import DeviceSelect from "./purchase-rent-plan/DeviceSelect.vue";
import { onMounted } from "vue";


const destinationStore = useDestinationStore();
const orderStore = useOrderStore();
const deviceStore = useDeviceStore();
const shipmentStore = useShipmentStore();
const periodStore = usePeriodStore();
const planStore = usePlanStore();

function getMinDate(type, index) {
    const periods = periodStore.periods;
    let date = new Date(Date.now());

    if (type === 'start') {
        if (index > 0) {
            let prevEndDate = periods[index - 1]?.endDate;
            if (prevEndDate) {
                date = new Date(prevEndDate);
                date.setDate(date.getDate());
            }
        }
    }

    if (type === 'end') {
        let startDate = periods[index]?.startDate;
        if (startDate) {
            date = new Date(startDate);
        }
    }

    return date;
}

function getWifiRentalPeriodText(index) {
    let planType = planStore.plan_type;
    let numberOfDays = periodStore.getPeriodDays(index);

    if (planType === 'month') {
        numberOfDays = periodStore.monthCount[index];
    } else if (periodStore.periods.length > 1) {
        const suffixes = ['st', 'nd', 'rd'];
        const getOrdinal = (n) => {
            const v = n + 1;
            if (v <= 3) return `${v}${suffixes[v - 1]}`;
            return `${v}th`;
        };
        const ordinal = getOrdinal(index);
        return `<span class="orange">${ordinal}</span> Destination Rental Period: <span class="orange">${numberOfDays}</span> ${planType}${numberOfDays > 1 ? 's' : ''}`;
    }

    let text = `Your WiFi Rental Period: <span class="orange">${numberOfDays}</span> ${planType}`;

    if (numberOfDays > 1) {
        text += 's';
    }

    return text;
}

function selectDestination(destination, index) {
    destinationStore.setSelectedDestinationByIndex({destination, index})
}

function dateSelect(date, index, type) {
    periodStore.setPeriodByIndex({
        index,
        field: type === 'start' ? 'startDate' : 'endDate',
        value: moment(date.date).startOf('day').format(),
    });
}

function monthQuantityChange(index) {
    periodStore.setEndDateByMonthCount(index);
}

function init() {

}

onMounted(() => {
    init();
});
</script>

<template>
    <div class="purchase-rent-plan">
        <div class="purchase-rent-plan__title">
            <h1>{{ $t("purchase-rent-page.plans.selectPlan") }}</h1>
        </div>

        <!-- Plan Type -->
        <PlanTypeSwitcher />

        <!-- Description -->
        <h5 class="purchase-rent-plan__description">
            {{ $t("purchase-rent-page.plans.description").replace('$country', destinationStore.mainSelectedDestination.name || 'your destination') }}
        </h5>

        <!-- Multi Destination Toggle -->
        <MultiDestinationToggle />

        <template v-for="(destination, index) in destinationStore.getSelectedDestinations">
            <div v-if="destination" class="purchase-rent-plan__body mb-8">
                <!-- Wifi Rental Period Text -->
                <h1 class="purchase-rent-plan__body-title select-none" v-html="getWifiRentalPeriodText(index)"></h1>
                <div class="br"></div>

                <!-- Periods -->
                <div class="purchase-rent-plan__body-date">
                    <!-- Start Date -->
                    <div class="purchase-rent-plan__body-date__start-date">
                        <h4>{{ $t("purchase-rent-page.plans.selectStartDate") }}</h4>
                        <h5>{{ $t("purchase-rent-page.plans.selectStartDateHint") }}</h5>

                        <DateSelect
                            v-model="periodStore.periods[index].startDate"
                            :masks="{input: 'MM/DD/YYYY'}"
                            :min-date="getMinDate('start', index)"
                            :placeholder="$t('purchase-rent-page.plans.selectStartDate')"
                            @dayclick="dateSelect($event, index, 'start')"
                        />
                    </div>
                    <template v-if="planStore.plan_type === 'day'">
                        <!-- End Date -->
                        <div class="purchase-rent-plan__body-date__start-date">
                            <h4>{{ $t("purchase-rent-page.plans.selectEndDate") }}</h4>
                            <h5>{{ $t("purchase-rent-page.plans.selectEndDateHint") }}</h5>

                            <DateSelect
                                v-model="periodStore.periods[index].endDate"
                                :masks="{input: 'MM/DD/YYYY'}"
                                :min-date="getMinDate('end', index)"
                                :placeholder="$t('purchase-rent-page.plans.selectEndDate')"
                                @dayclick="dateSelect($event, index, 'end')"
                            />
                        </div>
                    </template>
                    <!-- Month -->
                    <template v-if="planStore.plan_type === 'month'">
                        <div class="purchase-rent-plan__body-date__number-of-days">
                            <h4>{{ $t("purchase-rent-page.plans.numberOf").replace("$day", planStore.plan_type === 'month'
                                ? 'months' : 'days') }}</h4>
                            <h5>{{ $t("purchase-rent-page.plans.numberOfHint") }}</h5>

                            <QuantitySelect 
                                v-model="periodStore.monthCount[index]"
                                @onchange="monthQuantityChange(index)"
                            />
                        </div>
                    </template>
                </div>
                <div class="br"></div>

                <!-- Destinations -->
                <div class="mb-8">
                    <span class="w-full inline-block mb-3">Select your destination:</span>

                    <DestinationSelect 
                        v-model="destination.id"
                        @select-destination="selectDestination($event, index)" 
                    />
                </div>
                <div class="br"></div>

                <!-- Plans -->
                <h2>{{ $t("purchase-rent-page.plans.choosePlanType") }}</h2>
                <PlanSelect :plans="destination.packages" :index="index" />
            </div>
        </template>
    </div>
</template>

<style lang="scss">
.purchase-rent-plan {
    position: relative;
    margin-top: 2rem;

    &__title {
        margin-bottom: 2rem;

        h1 {
            text-align: left;
            font-size: 24px;
            font-weight: 400;
            margin-bottom: .5rem;
        }
    }

    &__description {
        font-weight: 300;
        font-size: 14px;
        margin-bottom: 1.5rem;
    }

    &__body {
        background: #ffffff;
        border: 2px solid #7DD3FC;
        border-radius: 8px;
        padding: 1.5rem 1rem;

        h2 {
            font-weight: 400;
            font-size: 1rem;
            margin-bottom: 1.5rem;
        }

        &-title {
            font-size: 1.8rem;
            font-weight: 400;
            margin-bottom: 1.5rem;

            .orange {
                color: #FE9634;
                font-weight: 500;
                font-size: 2.2rem;
            }
        }

        .br {
            border: 1px solid #7DD3FC;
            margin-bottom: 1.5rem;
            width: 100%;
        }

        &-date {
            display: flex;
            justify-content: space-between;
            margin-bottom: 1.5rem;
            width: 100%;

            h4 {
                font-size: 16px;
                font-weight: 400;
                margin-bottom: .5rem;
            }

            h5 {
                font-size: 14px;
                font-weight: 300;
                margin-bottom: 1rem;
            }

            &__start-date {
                width: 48%;

                &__input {
                    width: 100%;
                    box-sizing: border-box;
                    background: #ffffff;
                    border: 2px solid #FE9634;
                    border-radius: 8px;
                    padding: .8rem 3rem;
                    font-size: 1rem;
                    outline: none;
                }

                // .date-icon {
                //     position: absolute;
                //     top: .9rem;
                //     left: 1rem;
                // }
            }

            &__number-of-days {
                width: 48%;

                &__input {
                    background: #ffffff;
                    border: 2px solid #FE9634;
                    border-radius: 8px;
                    padding: 0 1rem;
                    height: 53px;
                    display: flex;
                    justify-content: space-between;
                    align-items: center;

                    .plus,
                    .minus {
                        cursor: pointer;
                    }

                    .number {
                        font-weight: 500;
                        font-size: 26px;
                        color: #FE9634;
                        user-select: none;
                    }
                }
            }
        }

        &-plan-types {
            display: flex;
            flex-wrap: wrap;
            margin-bottom: 1.5rem;
        }

        &-plan-type {
            background: #ffffff;
            width: 147px;
            padding: 1rem;
            border-radius: 8px;
            border: 2px solid #7DD3FC;
            cursor: pointer;
            margin: 0 1rem 1rem 0;

            &:hover,
            &.active {
                color: #ffffff;
                background: #7DD3FC;

                svg {
                    fill: #ffffff;
                }
            }

            &-info {
                font-size: 18px;
                font-weight: 500;
                margin-bottom: .5rem;
            }

            &-des {
                width: 100%;
                display: inline-block;
                font-size: 16px;
                font-weight: 300;
                margin-bottom: .5rem;
            }

            &-price {
                font-size: 16px;
                font-weight: 500;
            }

            &-note {
                position: relative;

                &__item {
                    display: none;
                    position: absolute;
                    bottom: 2.2rem;
                    left: -80px;
                    background-color: #ffffff;
                    font-size: 14px;
                    font-weight: 500;
                    color: #000000;
                    width: 200px;
                    max-width: 200px;
                    padding: .5rem 1rem;
                    border-radius: 8px;
                    border: 1px solid #7DD3FC;
                    text-align: center;
                }

                &:hover {
                    .purchase-rent-plan__body-plan-type-note__item {
                        display: block;
                    }
                }
            }
        }

        &-device-types {
            display: flex;
            flex-wrap: wrap;
            margin-bottom: 1.5rem;
        }

        &-device-type {
            border-radius: 8px;
            border: 2px solid #7DD3FC;
            background: #ffffff;
            padding: 1rem;
            margin: 0 1rem 1rem 0;
            width: 160px;
            display: flex;
            justify-content: center;
            flex-wrap: wrap;
            cursor: pointer;

            img {
                height: 64px;
                margin-bottom: .5rem;
            }

            h5 {
                width: 100%;
                text-align: center;
                font-size: 1rem;
            }

            &:hover,
            &.active {
                color: #ffffff;
                background: #7DD3FC;

                svg {
                    fill: #ffffff;
                }
            }

            &-note {
                position: relative;

                &__item {
                    display: none;
                    position: absolute;
                    bottom: 2.2rem;
                    left: -80px;
                    background-color: #ffffff;
                    font-size: 14px;
                    font-weight: 500;
                    color: #000000;
                    width: 200px;
                    max-width: 200px;
                    padding: .5rem 1rem;
                    border-radius: 8px;
                    border: 1px solid #7DD3FC;
                    text-align: center;
                }

                &:hover {
                    .purchase-rent-plan__body-device-type-note__item {
                        display: block;
                    }
                }
            }
        }

        &-device-quantity {
            width: 50%;

            h4 {
                font-size: 16px;
                font-weight: 400;
                margin-bottom: .5rem;
            }
        }
    }
}
</style>